import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Menu } from 'primereact/menu';
import { Skeleton } from 'primereact/skeleton';
import { 
    selectLoading, 
    selectResidenceList,
    selectProductList, 
    selectProductListCount, 
    getProductsAsync, 
    getProductsCountAsync, 
    deleteProductAsync, 
    editProductAsync,
    createProductAsync,
    initialLoadAsync,
} from './productSlice';
import { errorService } from '../../services/ErrorService';
import { showErrorToast, showSuccessToast } from '../../utils/ToastUtils';

const ProductsView = () => {

    let emptyProduct = {
        id: null,
        code: "",
        name: "",
        description: "",
        residenceId: 1,
        oralTestEnabled: false
    }

    const defaultGridParameters = {page: 0, first: 0, rows: 10, sortField: 'id', sortOrder: 1, filters: {}}
    
    const [deleteProductDialog, setDeleteProductDialog] = useState(false);
    
    const [productDialog, setProductDialog] = useState(false);
    const [product, setProduct] = useState(emptyProduct);
    const [selectedProducts, setSelectedProducts] = useState(null);

    const [gridParameters, setGridParameters] = useState(defaultGridParameters);

    const toast = useRef(null);
    const dt = useRef(null);
    const menu = useRef(null);
    const [detail, setDetail] = useState({});

    const dispatch = useDispatch();
    const loading = useSelector(selectLoading);
    const residenceList = useSelector(selectResidenceList);
    const productList = useSelector(selectProductList);
    const productListCount = useSelector(selectProductListCount);

    useEffect(() => {
        const loadInitialData = async () => {
            try {
                await errorService.retryOperation(async () => {
                    await dispatch(initialLoadAsync(defaultGridParameters)).unwrap();
                });
            } catch (error) {
                showErrorToast(toast, 'Erro ao carregar produtos', error);
            }
        };

        loadInitialData();
    }, [dispatch]);

    const onSort = async (event) => {
        try {
            await errorService.retryOperation(async () => {
                const sortParams = {
                    ...gridParameters,
                    sortField: event.sortField,
                    sortOrder: event.sortOrder
                };
                setGridParameters(sortParams);
                await Promise.all([
                    dispatch(getProductsAsync(sortParams)).unwrap(),
                    dispatch(getProductsCountAsync(sortParams)).unwrap()
                ]);
            });
        } catch (error) {
            showErrorToast(toast, 'Erro ao ordenar produtos', error);
        }
    };

    const onFilter = async (event) => {
        try {
            await errorService.retryOperation(async () => {
                const filterParams = {
                    ...gridParameters,
                    filters: event.filters
                };
                setGridParameters(filterParams);
                await Promise.all([
                    dispatch(getProductsAsync(filterParams)).unwrap(),
                    dispatch(getProductsCountAsync(filterParams)).unwrap()
                ]);
            });
        } catch (error) {
            showErrorToast(toast, 'Erro ao filtrar produtos', error);
        }
    };

    const onPage = async (event) => {
        try {
            await errorService.retryOperation(async () => {
                const pageParams = {
                    ...gridParameters,
                    first: event.first,
                    rows: event.rows
                };
                setGridParameters(pageParams);
                await dispatch(getProductsAsync(pageParams)).unwrap();
            });
        } catch (error) {
            showErrorToast(toast, 'Erro ao mudar página', error);
        }
    };

    const openNew = () => {
        setProduct(emptyProduct);
        setProductDialog(true);
    }

    const hideDialog = () => {
        setProductDialog(false);
    }

    const hideProductDialog = () => {
        setProductDialog(false);
    }

    const hideDeleteProductDialog = () => {
        setDeleteProductDialog(false);
    }

    const saveProduct = async () => {
        try {
            await errorService.retryOperation(async () => {
                if (product.id) {
                    await dispatch(editProductAsync({product, gridParameters})).unwrap();
                    showSuccessToast(toast, 'Sucesso', 'Produto atualizado com sucesso');
                } else {
                    await dispatch(createProductAsync({product, gridParameters})).unwrap();
                    showSuccessToast(toast, 'Sucesso', 'Produto criado com sucesso');
                }
                hideDialog();
                await dispatch(getProductsAsync(gridParameters)).unwrap();
            });
        } catch (error) {
            showErrorToast(toast, 'Erro ao salvar produto', error);
        }
    };

    const editProduct = (product) => {
        setProduct({ ...product });
        setProductDialog(true);
    }

    const confirmDeleteProduct = (product) => {
        setProduct(product);
        setDeleteProductDialog(true);
    }

    const deleteProduct = async () => {
        try {
            await errorService.retryOperation(async () => {
                await dispatch(deleteProductAsync({id: product.id, gridParameters})).unwrap();
                showSuccessToast(toast, 'Sucesso', 'Produto excluído com sucesso');
                hideDeleteProductDialog();
                await dispatch(getProductsAsync(gridParameters)).unwrap();
            });
        } catch (error) {
            showErrorToast(toast, 'Erro ao excluir produto', error);
        }
    };

    const exportCSV = () => {
        dt.current.exportCSV();
    }

    const confirmDeleteSelected = () => {
        // setDeleteProductsDialog(true);
    }

    const onInputChangeProduct = (e, name) => {
        const val = (e.target && e.target.value) || '';
        let _product = { ...product };
        _product[`${name}`] = val;
        setProduct(_product);
    }

    const leftToolbarTemplate = () => {
        return (
            <React.Fragment>
                <div className="my-2">
                    <Button label="Novo produto" icon="pi pi-plus" className="p-button-success mr-2" onClick={openNew} />
                    {/* <Button label="Delete" icon="pi pi-trash" className="p-button-danger" onClick={confirmDeleteSelected} disabled={!selectedProducts || !selectedProducts.length} /> */}
                </div>
            </React.Fragment>
        )
    }

    const residenceBodyTemplate = (rowData) => {
        return (
            <>
                <span className="p-column-title">Residência</span>
                {residenceList.find(it => it.id == rowData.residenceId).description}
            </>
        );
    }

    const toggleMenu = (event, data) => {
        setDetail(data)
        menu.current.toggle(event);
    };

    const toolbarItems = [
        {
            label: 'Editar',
            icon: 'pi pi-pencil',
            command: () => editProduct(detail)
        },
        {
            label: 'Excluir',
            icon: 'pi pi-trash',
            command: () => confirmDeleteProduct(detail)
        }
    ];

    const actionBodyTemplate = (rowData) => {
        return <>
            <Menu ref={menu} model={toolbarItems} popup />
            <Button type="button" label="Ações" icon="pi pi-angle-down" onClick={(event) => toggleMenu(event, rowData)} style={{ width: 'auto' }} />
        </>;
    }

    const productDialogFooter = (
        <>
            <Button label="Cancelar" icon="pi pi-times" className="p-button-text" onClick={hideProductDialog} />
            <Button label="Salvar" icon="pi pi-check" className="p-button-text" onClick={saveProduct} />
        </>
    );

    const deleteProductDialogFooter = (
        <>
            <Button label="Não" icon="pi pi-times" className="p-button-text" onClick={hideDeleteProductDialog} />
            <Button label="Sim" icon="pi pi-check" className="p-button-text" onClick={deleteProduct} />
        </>
    );

    const oralTestEnabledBodyTemplate = (rowData) => {
        return (
            <>
                <span className="p-column-title">Prova Oral habilitado?</span>
                {rowData.oralTestEnabled ? 'Sim' : 'Não'}
            </>
        );
    };

    const onRefresh = async () => {
        try {
            await errorService.retryOperation(async () => {
                await Promise.all([
                    dispatch(getProductsAsync(gridParameters)).unwrap(),
                    dispatch(getProductsCountAsync(gridParameters)).unwrap()
                ]);
            });
        } catch (error) {
            showErrorToast(toast, 'Erro ao carregar dados', error);
        }
    };

    return (
      <>
        <div className="grid crud-demo">
            <div className="col-12">
                <div className="card">
                    <Toast ref={toast} />
                    <div className="flex justify-content-between align-items-center mb-3">
                        <h5 className="m-0">Gestão de produtos</h5>
                        <Button
                            icon="pi pi-refresh"
                            rounded
                            text
                            onClick={onRefresh}
                            disabled={loading}
                        />
                    </div>
                    <DataTable ref={dt} value={loading ? Array.from({ length: gridParameters.rows }, (_, i) => ({ id: `skeleton-${i}` })) : productList} selection={selectedProducts} onSelectionChange={(e) => setSelectedProducts(e.value)}
                        header={leftToolbarTemplate()}
                        dataKey="id"
                        paginator
                        sortField={gridParameters.sortField}
                        sortOrder={gridParameters.sortOrder}
                        rows={gridParameters.rows}
                        rowsPerPageOptions={[5, 10, 25, 50, 100]}
                        className="datatable-responsive"
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                        currentPageReportTemplate="Exibindo de {first} à {last} de {totalRecords} registros"
                        emptyMessage="Sem resultados."
                        responsiveLayout="scroll"
                        totalRecords={productListCount}
                        lazy={true}
                        first={gridParameters.first}
                        onSort={(e) => onSort(e)}
                        onFilter={(e) => onFilter(e)}
                        onPage={(e) => onPage(e)}
                        >
                        <Column field="code" header="Código" sortable={!loading} body={loading ? () => <Skeleton /> : undefined} headerStyle={{ width: '1em', minWidth: '10rem' }}></Column>
                        <Column field="name" header="Nome" sortable={!loading} body={loading ? () => <Skeleton /> : undefined} headerStyle={{ width: '1em', minWidth: '10rem' }}></Column>
                        <Column field="description" header="Descrição" sortable={!loading} body={loading ? () => <Skeleton /> : undefined} headerStyle={{ width: '1em', minWidth: '10rem' }}></Column>
                        <Column field="residenceId" header="Residência" body={loading ? () => <Skeleton /> : residenceBodyTemplate} headerStyle={{ width: '1em', minWidth: '10rem' }}></Column>
                        <Column field="oralTestEnabled" header="Prova Oral habilitado?" body={loading ? () => <Skeleton /> : oralTestEnabledBodyTemplate} headerStyle={{ width: '1em', minWidth: '10rem' }}></Column>
                        <Column body={loading ? () => <Skeleton /> : actionBodyTemplate} style={{ width: '15%', minWidth: '10rem' }}></Column>
                    </DataTable>

                    {/* Editar Produto */}
                    <Dialog visible={productDialog} style={{ width: '450px' }} header={`${product.id ? 'Editar' : 'Criar'} produto`} modal className="p-fluid" footer={productDialogFooter} onHide={hideProductDialog}>

                        <div className="field">
                            <label htmlFor="code">Código</label>
                            <InputText id="code" value={product.code} onChange={(e) => onInputChangeProduct(e, 'code')} required />
                        </div>

                        <div className="field">
                            <label htmlFor="name">Nome</label>
                            <InputText id="name" value={product.name} onChange={(e) => onInputChangeProduct(e, 'name')} required />
                        </div>

                        <div className="field">
                            <label htmlFor="description">Descrição</label>
                            <InputTextarea id="description" value={product.description} onChange={(e) => onInputChangeProduct(e, 'description')} required rows={3} cols={20} />
                        </div>

                        <div className="field">
                            <label htmlFor="residenceId">Residência</label>
                            <Dropdown id="residenceId" value={product.residenceId} onChange={(e) => onInputChangeProduct(e, 'residenceId')} options={residenceList} optionLabel="description" optionValue="id" placeholder="Selecione" />
                        </div>

                        <div className="field">
                            <label htmlFor="oralTestEnabled">Habilitar Prova Oral?</label>
                            <div className="formgrid grid">
                                <div className="field-radiobutton col-6">
                                    <input
                                        type="radio"
                                        id="oralTestEnabledFalse"
                                        name="oralTestEnabled"
                                        value={false}
                                        checked={!product.oralTestEnabled}
                                        onChange={() => setProduct({ ...product, oralTestEnabled: false })}
                                    />
                                    <label htmlFor="oralTestEnabledFalse">Não</label>
                                </div>
                                <div className="field-radiobutton col-6">
                                    <input
                                        type="radio"
                                        id="oralTestEnabledTrue"
                                        name="oralTestEnabled"
                                        value={true}
                                        checked={product.oralTestEnabled}
                                        onChange={() => setProduct({ ...product, oralTestEnabled: true })}
                                    />
                                    <label htmlFor="oralTestEnabledTrue">Sim</label>
                                </div>
                            </div>
                        </div>

                    </Dialog>
                    {/* FIM - Editar Produto */}

                    <Dialog visible={deleteProductDialog} style={{ width: '450px' }} header="Confirmar exclusão" modal footer={deleteProductDialogFooter} onHide={hideDeleteProductDialog}>
                        <div className="flex align-items-center justify-content-center">
                            <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
                            {product && <span>Você deseja realmente excluir: <b>{product.name}</b>?</span>}
                        </div>
                    </Dialog>
                </div>
            </div>
        </div>
      </>
    );
}

const comparisonFn = function (prevProps, nextProps) {
    return prevProps.location.pathname === nextProps.location.pathname;
};

export default React.memo(ProductsView, comparisonFn);